import { React, useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, List, ListItem, ListItemButton, ListItemText, Typography, Collapse, CircularProgress } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import RoundIndicatorIcon from "../../../assets/icons/RoundIndicatorIcon";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function OfferBoardsSidebar({
  activeUser,
  isAdminUser,
  loads,
  setRefreshLoads,
  setRefreshPage,
  selectedOrderId,
  setSelectedOrderId
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingLoads, setPendingLoads] = useState([]);
  const [acceptedLoads, setAcceptedLoads] = useState([]);
  const [clearedLoads, setClearedLoads] = useState([]);
  const [openPending, setOpenPending] = useState(true);
  const [openAccepted, setOpenAccepted] = useState(true);
  const [openCleared, setOpenCleared] = useState(true);
  const expansionArrowViewBox = "-2 2 20 20";

  const clearLoads = () => {
    if (isLoading || pendingLoads.length === 0) return;
    pendingLoads.forEach(load => {
      let payload = {
        orderId: load.orderId,
        userId: activeUser.userIdMcld,
        isManager: isAdminUser
      };

      setOpenPending(false);
      setOpenCleared(true);
      setIsLoading(true);
      CarrierService.ClearLoads(instance, payload).then(() => {
        setIsLoading(false);
        setRefreshLoads(true);
        setRefreshPage(true);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    });
  };

  const unclearLoads = () => {
    if (isLoading || clearedLoads.length === 0) return;
    clearedLoads.forEach(load => {
      let payload = {
        orderId: load.orderId,
        userId: activeUser.userIdMcld,
        isManager: isAdminUser
      };

      setOpenCleared(false);
      setOpenPending(true);
      setIsLoading(true);
      CarrierService.UnclearLoads(instance, payload).then(() => {
        setIsLoading(false);
        setRefreshPage(true);
        setRefreshLoads(true);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    });
  };

  useEffect(() => {
    if (loads.length) {
      setPendingLoads(loads.filter(({ status }) => status === "pending"));
      setAcceptedLoads(loads.filter(({ status }) => status === "approved"));
      setClearedLoads(loads.filter(({ status }) => status === "cleared"));
    } else {
      setPendingLoads([]);
      setAcceptedLoads([]);
      setClearedLoads([]);
    }
  }, [loads]);

  return (
    <List>
      <ListItemButton onClick={() => setOpenPending(!openPending)}>
        <ListItemText>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="label1" color="black_opacity.100">
              Pending ({pendingLoads.length})
            </Typography>
            <Typography
              variant="helperText"
              sx={{ color: isLoading || pendingLoads.length === 0 ? "black_solid.30" : "primary.60" }}
              onClick={(e) => {
                e.stopPropagation();
                clearLoads();
              }}>
              Clear
            </Typography>
          </Box>
        </ListItemText>
        {openPending
          ? <ArrowDropUpIcon viewBox={expansionArrowViewBox} />
          : <ArrowDropDownIcon viewBox={expansionArrowViewBox} />
        }
      </ListItemButton>
      <Collapse in={openPending}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }} >
            <CircularProgress sx={{ color: "primary.60" }} size={30} />
          </Box>
        ) : (
          <List>
            {pendingLoads.length > 0 ? pendingLoads.map((r) => (
              <ListItemButton
                key={`pending-${r.orderId}`}
                onClick={() => setSelectedOrderId(r.orderId)}
                selected={selectedOrderId === r.orderId}
              >
                <ListItemText>
                  <Typography variant="body1" color="black_opacity.100">
                    {r.orderId} ({r.originState} &gt; {r.destinationState})
                  </Typography>
                </ListItemText>
              </ListItemButton>
            )) : (
              <ListItem>
                <ListItemText primary="No loads found" sx={{ color: "black_solid.50" }} />
              </ListItem>
            )}
          </List>
        )}
      </Collapse>
      <ListItemButton onClick={() => setOpenAccepted(!openAccepted)}>
        <ListItemText>
          <Typography variant="label1" color="black_opacity.100">
            Accepted ({acceptedLoads.length})
          </Typography>
        </ListItemText>
        {openAccepted
          ? <ArrowDropUpIcon viewBox={expansionArrowViewBox} />
          : <ArrowDropDownIcon viewBox={expansionArrowViewBox} />
        }
      </ListItemButton>
      <Collapse in={openAccepted}>
        <List>
          {acceptedLoads.length > 0 ? acceptedLoads.map((r) => (
            <ListItemButton
              key={`accepted-${r.orderId}`}
              onClick={() => setSelectedOrderId(r.orderId)}
              selected={selectedOrderId === r.orderId}
            >
              <ListItemText>
                <Typography variant="body1" color="black_opacity.100" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  {r.orderId} ({r.originState} &gt; {r.destinationState})
                  {r.newlyAdded && <RoundIndicatorIcon sx={{ color: "primary.60" }} viewBox="0 -5 24 24" />}
                </Typography>
              </ListItemText>
            </ListItemButton>
          )) : (
            <ListItem>
              <ListItemText primary="No loads found" sx={{ color: "black_solid.50" }} />
            </ListItem>
          )}
        </List>
      </Collapse>
      <ListItemButton onClick={() => setOpenCleared(!openCleared)}>
        <ListItemText>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="label1" color="black_opacity.100">
              Cleared ({clearedLoads.length})
            </Typography>
            <Typography
              variant="helperText"
              sx={{ color: isLoading || clearedLoads.length === 0 ? "black_solid.30" : "primary.60" }}
              onClick={(e) => {
                e.stopPropagation();
                unclearLoads();
              }}>
              Unclear
            </Typography>
          </Box>
        </ListItemText>
        {openCleared
          ? <ArrowDropUpIcon viewBox={expansionArrowViewBox} />
          : <ArrowDropDownIcon viewBox={expansionArrowViewBox} />
        }
      </ListItemButton>
      <Collapse in={openCleared}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }} >
            <CircularProgress sx={{ color: "primary.60" }} size={30} />
          </Box>
        ) : (
          <List>
            {clearedLoads.length > 0 ? clearedLoads.map((r) => (
              <ListItemButton
                key={`cleared-${r.orderId}`}
                onClick={() => setSelectedOrderId(r.orderId)}
                selected={selectedOrderId === r.orderId}
              >
                <ListItemText>
                  <Typography variant="body1" color="black_opacity.100">
                    {r.orderId} ({r.originState} &gt; {r.destinationState})
                  </Typography>
                </ListItemText>
              </ListItemButton>
            )) : (
              <ListItem>
                <ListItemText primary="No loads found" sx={{ color: "black_solid.50" }} />
              </ListItem>
            )}
          </List>
        )}
      </Collapse>
    </List>
  );
}

OfferBoardsSidebar.propTypes = {
  activeUser: PropTypes.object,
  isAdminUser: PropTypes.bool,
  loads: PropTypes.array.isRequired,
  setRefreshLoads: PropTypes.func,
  setRefreshPage: PropTypes.func,
  selectedOrderId: PropTypes.string,
  setSelectedOrderId: PropTypes.func.isRequired
};